import { useEffect, useRef, useCallback } from "react";
import { toast } from "../../../utils/notifications";
import type { RFQItem } from "../../../types/rfq";
import type { TeamMember } from "../../../types";
import type { RFQItemUpdate } from "../../../services/rfqItems";

interface UseItemValidationOptions {
  item: RFQItem;
  selectedUser: TeamMember | null;
  /** The price input's current text. */
  price: string;
  isPriceModified: boolean;
  setPrice: (price: string) => void;
  setIsPriceModified: (modified: boolean) => void;
  setIsRemarksModified: (modified: boolean) => void;
  /** Writes the row (the row's update mutation). */
  save: (data: RFQItemUpdate) => void;
}

/**
 * Validating (and unvalidating) the item's price or message, and marking the
 * item not available ($0, validated). Clicks are debounced for 300 ms.
 */
export function useItemValidation({
  item,
  selectedUser,
  price,
  isPriceModified,
  setPrice,
  setIsPriceModified,
  setIsRemarksModified,
  save,
}: UseItemValidationOptions) {
  // Handle validation toggles - memoized for better performance with optimistic updates
  const handleValidation = useCallback((type: 'price' | 'message') => {
    if (!selectedUser) {
      toast({
        title: "Error validating item",
        description: "No user selected. Please select a user before validating.",
        severity: "error",
      });
      return;
    }

    // Create update data based on validation type
    const updateData: RFQItemUpdate = {};
    const now = new Date().toISOString();
    
    if (type === 'price') {
      updateData.price_validated = !item.price_validated;
      // The server's price unless the user changed it here: the local input
      // can lag a server-side change, and validating must not write it back.
      const numericPrice = !isPriceModified && item.quoted_price != null
        ? Number(item.quoted_price)
        : parseFloat(price);
      
      // Block validation if price is empty/invalid (prevent NaN in database)
      if (!item.price_validated && isNaN(numericPrice)) {
        toast({
          title: "Invalid price",
          description: "Please enter a valid price before validating.",
          severity: "error",
        });
        return;
      }

      updateData.quoted_price = numericPrice;
      
      // Add validation info when validating (not when unvalidating)
      if (!item.price_validated) {
        updateData.validated_by = selectedUser.name;
        updateData.validated_at = now;
      } else {
        // When unvalidating, clear the validation info
        updateData.validated_by = null;
        updateData.validated_at = null;
      }
      // Reset price modification state when validating
      setIsPriceModified(false);
    } else if (type === 'message') {
      updateData.message_validated = !item.message_validated;
      // Add validation info when validating (not when unvalidating)
      if (!item.message_validated) {
        updateData.validated_by = selectedUser.name;
        updateData.validated_at = now;
      } else {
        // When unvalidating, clear the validation info
        updateData.validated_by = null;
        updateData.validated_at = null;
      }
      // Reset remarks modification state when validating
      setIsRemarksModified(false);
    }

    // Trigger mutation - optimistic update will make UI change instantly
    save(updateData);
  }, [selectedUser, item.price_validated, item.message_validated, item.quoted_price, isPriceModified, price, save, setIsPriceModified, setIsRemarksModified]);

  // Handle mark as item not available (sets price to 0 and validates)
  const handleMarkItemNotAvailable = () => {
    if (!selectedUser) {
      toast({
        title: "Error marking item",
        description: "No user selected. Please select a user before marking items.",
        severity: "error",
      });
      return;
    }
    
    const now = new Date().toISOString();
    const updateData = {
      quoted_price: 0,
      price_validated: true,
      validated_by: selectedUser.name,
      validated_at: now,
    };
    
    // Update local state
    setPrice('0.00');
    setIsPriceModified(false);
    
    // Optimistic update will make this instant
    save(updateData);
    
    toast({
      title: "Item marked as not available",
      description: "Price set to $0.00 and validated",
      severity: "info",
    });
  };

  const validationClickTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Optimized validation click handler with debounce
  const handleValidationClick = useCallback((type: 'price' | 'message') => {
    // Prevent double-clicks
    if (validationClickTimeoutRef.current) {
      return;
    }

    // Set a short timeout to prevent rapid clicks
    validationClickTimeoutRef.current = setTimeout(() => {
      validationClickTimeoutRef.current = null;
    }, 300);

    handleValidation(type);
  }, [handleValidation]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (validationClickTimeoutRef.current) {
        clearTimeout(validationClickTimeoutRef.current);
      }
    };
  }, []);

  return { handleValidationClick, handleMarkItemNotAvailable };
}
